/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { wrapStJudeGlobal, type StJudeDigitalData } from '@/lib/tracking/stjude-datalayer';

export interface StJudeDataLayerProps {
    omnitureEnabled?: boolean;
    digitalData: StJudeDigitalData;
    /** CSP nonce for inline scripts; null/undefined when security headers middleware is disabled. */
    nonce?: string | null;
}

const LINE_SEPARATOR = String.fromCharCode(0x2028);
const PARAGRAPH_SEPARATOR = String.fromCharCode(0x2029);

/**
 * Injects the `window.stjude` Omniture/Adobe Launch dataLayer into
 * `document.head` — the Storefront Next equivalent of legacy Sitegenesis's
 * `var stjude = {...}` output in `lib/datalayer.isml`. Adobe Launch rules
 * read this global directly, so it must exist before the Launch library
 * (injected by `OmnitureHeaderInclude`) executes — render this component
 * immediately before `<OmnitureHeaderInclude>` in `<head>`.
 *
 * This only covers the *first* document parse (full page load/SSR). Browsers
 * execute a `<script>` element's body when it's first inserted, not when its
 * content is mutated afterward — so re-rendering this component with new
 * `digitalData` on a client-side (SPA) navigation updates the DOM node's
 * text but does NOT re-run it, leaving `window.stjude` frozen at whatever
 * page loaded first. `StJudeDataLayerSync` (src/analytics/stjude-datalayer-sync.tsx)
 * is what keeps `window.stjude` current across client-side navigation —
 * mounted once near the app root, it reassigns the global directly via
 * `useEffect` on every `useLocation()` change, the same pattern
 * `PageViewTracker` uses for the same reason. Category, product, and
 * checkout pages further correct `page` once their own dedicated sync
 * component mounts (breadcrumb/step data those routes own but the root
 * loader doesn't have) — see `StJudeCategoryDataLayerSync`,
 * `StJudeProductDataLayerSync`, and `StJudeCheckoutDataLayerSync`.
 *
 * Gated on the same `omnitureEnabled` flag as `OmnitureHeaderInclude`/
 * `OmnitureFooterInclude` (from the shared tracking-config Custom API), so
 * the feature is remotely toggleable without a deploy. Renders nothing when
 * disabled.
 */
export function StJudeDataLayer({ omnitureEnabled, digitalData, nonce }: StJudeDataLayerProps) {
    if (!omnitureEnabled) return null;

    // Same escaping as `window.__APP_CONFIG__` in root.tsx: prevents a `</script>`
    // or HTML-comment-like sequence inside a value from breaking out of the
    // inline script, and normalizes line/paragraph separators the JSON spec
    // allows but JS string literals historically didn't.
    const json = JSON.stringify(wrapStJudeGlobal(digitalData))
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026')
        .split(LINE_SEPARATOR)
        .join('\\u2028')
        .split(PARAGRAPH_SEPARATOR)
        .join('\\u2029');

    return (
        <script
            nonce={nonce ?? undefined}
            suppressHydrationWarning
            // Controlled inline script (server-built JSON, escaped above) — not user input.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: `window.stjude = ${json};` }}
        />
    );
}
